import { createEnv } from "@t3-oss/env-core";
import * as R from "remeda";
import * as z from "zod/mini";

const runtimeEnv = import.meta.env ?? process.env;

const isProd = runtimeEnv.REACT_APP_ENV === "production";
const isStaging = runtimeEnv.REACT_APP_ENV === "staging";
const isTest = runtimeEnv.NODE_ENV === "test";
const isKnip = runtimeEnv.NODE_ENV === "knip";

const baseUrlSchema = z.catch(z.optional(z.url()), undefined);
const TRAILING_SLASH_REGEX = /\/$/;
const LEADING_DOTS_REGEX = /^\.+/;
const DEFAULT_ALLOWED_REDIRECT_HOSTS = ["qwick.com"] as const;
export const DEFAULT_SHIFTOPS_ALLOWED_REDIRECT_HOSTS = ["shiftops.ai", "cadencework.ai"] as const;

export function parseAllowedHosts(raw: string | undefined, fallback: ReadonlyArray<string>): ReadonlyArray<string> {
  if (!raw) return fallback;
  const parsed = raw
    .split(",")
    .map((h) => h.trim().toLowerCase().replace(LEADING_DOTS_REGEX, ""))
    .filter(Boolean);
  return parsed.length > 0 ? parsed : fallback;
}

const serverEnv = {
  ANALYZE: z._default(z.stringbool(), false),
  CI: z._default(z.stringbool(), false),
  SENTRY_AUTH_TOKEN: z.optional(z.string()),
  SOURCE_VERSION: z.optional(z.string()),

  BASE_URL: baseUrlSchema,
};

const validatedEnv = createEnv({
  server: serverEnv,

  clientPrefix: "REACT_APP_",
  client: {
    REACT_APP_ENV: z._default(z.enum(["production", "staging", "development", "automation", "local", "test"]), "local"),
    REACT_APP_SG_API_URL: z.pipe(
      z.url(),
      z.transform((url) => (url.endsWith("/") ? url : `${url}/`)),
    ),
    REACT_APP_STRIPE_KEY: z.string(),
    REACT_APP_CLOUDINARY_CLOUD_NAME: z.string(),
    REACT_APP_DEFAULT_LOCATION_IMAGE_ID: z.string(),
    REACT_APP_QWICK_MCP_SERVER_URL: z._default(
      z.pipe(
        z.optional(z.string()),
        z.transform((url) => url?.replace(TRAILING_SLASH_REGEX, "")),
      ),
      "https://qwick-mcp-staging.qwick.com",
    ),
    /** First-party AI chat only; overrides REACT_APP_QWICK_MCP_SERVER_URL and may be relative (`/__mcp`). */
    REACT_APP_QWICK_MCP_CHAT_URL: z.pipe(
      z.optional(z.string()),
      z.transform((url) => url?.replace(TRAILING_SLASH_REGEX, "")),
    ),

    /**
     * Apex domains post-login ?returnTo may redirect to after successful auth.
     * Each entry matches the apex itself AND any subdomain over HTTPS (e.g.
     * "qwick.com" allows qwick.com + *.qwick.com). Read by navigateToReturnTo
     * in domains/auth/queries/helpers.ts.
     *
     * Configured via REACT_APP_AUTH_ALLOWED_REDIRECT_HOSTS as a comma-separated
     * list. Inlined at build time, so not a secret — it's an allowlist that
     * ships in the client bundle. Defaults to "qwick.com" when unset to
     * preserve current behavior.
     *
     * Parsing is fail-closed: trimming, casing, and empty entries are
     * stripped; if the resulting list is empty (misconfigured deploy) we
     * fall back to the hardcoded default rather than allowing nothing
     * (breaks prod) or allowing everything (reopens the redirect hole).
     */
    REACT_APP_AUTH_ALLOWED_REDIRECT_HOSTS: z.pipe(
      z.optional(z.string()),
      z.transform((raw): ReadonlyArray<string> => parseAllowedHosts(raw, DEFAULT_ALLOWED_REDIRECT_HOSTS)),
    ),

    /**
     * Apex domains the /link/shiftops consent page may redirect to after a
     * confirm/cancel — the ShiftOps console origin(s), e.g. "shiftops.ai" or
     * "cadencework.ai" allows the apex + its subdomains (*.shiftops.ai,
     * *.cadencework.ai). Same allowlist shape/parsing as
     * REACT_APP_AUTH_ALLOWED_REDIRECT_HOSTS (fail-closed, comma-separated),
     * kept as its own var since it gates a different, unrelated redirect
     * target than post-login returnTo. Defaults to both shiftops.ai and
     * cadencework.ai (the ShiftOps console's post-rebrand domain) when unset.
     */
    REACT_APP_SHIFTOPS_ALLOWED_REDIRECT_HOSTS: z.pipe(
      z.optional(z.string()),
      z.transform((raw): ReadonlyArray<string> => parseAllowedHosts(raw, DEFAULT_SHIFTOPS_ALLOWED_REDIRECT_HOSTS)),
    ),

    REACT_APP_ZOOM_CC_API_KEY_W2: z.optional(z.string()),
    REACT_APP_GOOGLE_API_KEY: z.string(),
    /** Google Cloud map ID, needed for draggable markers; falls back to Google's demo ID. */
    REACT_APP_GOOGLE_MAP_ID: z.optional(z.string()),
    REACT_APP_SENDBIRD_APP_ID: z.string(),
    REACT_APP_LAUNCH_DARKLY_ID: z.string(),
    REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE: z.optional(z.string()),
    REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL: z.optional(z.url()),

    REACT_APP_BUSINESS_SUPPORT_URL: z.url(),
    REACT_APP_CONTACT_SUPPORT_URL: z.url(),
    REACT_APP_MARKETPLACE_PLATFORM_TERMS_URL: z.url(),
    REACT_APP_BUSINESS_SERVICES_AGREEMENT_URL: z.url(),
    REACT_APP_PRIVACY_POLICY_URL: z.url(),

    REACT_APP_SENTRY_DSN: z.optional(z.string()),

    REACT_APP_SEGMENT_WRITE_KEY: z.optional(z.string()),
    REACT_APP_SEGMENT_CDN_URL: z.optional(z.url()),

    REACT_APP_CARTO_BASEMAP_KEY: z.optional(z.string()),

    REACT_APP_BRAZE_KEY: z.optional(z.string()),
    REACT_APP_BRAZE_ENDPOINT: z.optional(z.string()),

    REACT_APP_DEV_HTTPS: z._default(z.stringbool(), true),
  },
  createFinalSchema: (shape) => {
    const schema = z.object(shape);

    if (isTest) {
      // If we are in test mode, we want to allow most env variables to be optional but without breaking the type
      return z.required(z.partial(schema), {
        REACT_APP_ENV: true,
        REACT_APP_CLOUDINARY_CLOUD_NAME: true,
        REACT_APP_DEFAULT_LOCATION_IMAGE_ID: true,
      }) as unknown as typeof schema;
    }
    if (!isProd && !isStaging) {
      // if REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE is set we don't need REACT_APP_LAUNCH_DARKLY_ID
      if (runtimeEnv.REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE) {
        return z.extend(z.omit(schema, { REACT_APP_LAUNCH_DARKLY_ID: true }), {
          REACT_APP_LAUNCH_DARKLY_ID: z._default(z.optional(shape.REACT_APP_LAUNCH_DARKLY_ID), ""),
        });
      }
      return schema;
    }
    const requiredKeys = {
      REACT_APP_QWICK_MCP_SERVER_URL: true,
      REACT_APP_ZOOM_CC_API_KEY_W2: true,
      REACT_APP_SEGMENT_WRITE_KEY: true,
      REACT_APP_BRAZE_KEY: true,
      REACT_APP_BRAZE_ENDPOINT: true,
      REACT_APP_SENTRY_DSN: true,
      SENTRY_AUTH_TOKEN: true,
    } as const;
    // Zod v4 .required() errors on keys not in the shape — filter to only keys present
    return z.required(
      schema,
      R.pickBy(requiredKeys, (_, key) => key in shape),
    );
  },
  runtimeEnv,
  emptyStringAsUndefined: true,
  skipValidation: isKnip,
});

export const env = {
  ...validatedEnv,
  IS_LOCAL: runtimeEnv.DEV,
  IS_DEV: !isProd && validatedEnv.REACT_APP_ENV !== "staging",
  IS_PROD: isProd,
  IS_AUTOMATION: isTest || validatedEnv.REACT_APP_ENV === "automation" || validatedEnv.REACT_APP_ENV === "test",
  IS_KNIP: isKnip,
};
